import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";
import { ShieldCheck, User, UserPlus, KeyRound, Eye, EyeOff, QrCode, Loader2, Building2, Clock, CheckCircle2, Lock, Sun, Mountain, Handshake, Printer, ScanLine } from "lucide-react";
import { BrandLogo } from "@/components/Header";

const ID_TYPES = ["Aadhaar", "PAN Card", "Passport", "Driving Licence", "Corporate ID"];
const CARD_ICONS = [Sun, Mountain, Handshake];

export default function AccessPortal() {
  const { t, login } = useApp();
  const navigate = useNavigate();
  const [mode, setMode] = useState("employee");
  const p = t.portalX;

  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-40" />
      <section className="relative oj-container py-12">
        {/* status bar */}
        <div className="flex flex-wrap items-center gap-4 mb-8 font-mono text-[11px] text-teal-700">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-teal-400 pulse-dot" /> {p.gateway}</span>
          <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {p.ssl}</span>
          <span className="px-2 py-0.5 rounded border border-teal-500/50 text-teal-700">ISO 27001</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* left panel */}
          <Reveal className="lg:col-span-2">
            <div className="oj-card p-8 h-full">
              <div className="mb-6">
                <BrandLogo className="h-14" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">{t.portalPage.title}</h1>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">{p.leftDesc}</p>
              <div className="space-y-3">
                {p.cards.map(([title, desc], i) => {
                  const Icon = CARD_ICONS[i];
                  return (
                    <div key={title} className="rounded-xl border border-teal-200 bg-teal-50 p-4 flex gap-3">
                      <Icon className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                      <div><div className="font-semibold text-white text-sm">{title}</div><div className="text-xs text-slate-600 mt-0.5">{desc}</div></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          {/* right panel */}
          <Reveal delay={0.1} className="lg:col-span-3">
            <div className="oj-card p-8">
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-8">
                <button onClick={() => setMode("employee")} data-testid="portal-tab-employee"
                  className={`px-2 sm:px-4 py-3 sm:py-3.5 rounded-xl text-[11px] sm:text-sm font-semibold flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 transition-all ${mode === "employee" ? "bg-teal-600 text-white shadow-lg" : "bg-teal-50 text-slate-600 border border-teal-200"}`}>
                  <User className="w-4 h-4 shrink-0" /> {t.portalPage.employee}
                </button>
                <button onClick={() => setMode("visitor")} data-testid="portal-tab-visitor"
                  className={`px-2 sm:px-4 py-3 sm:py-3.5 rounded-xl text-[11px] sm:text-sm font-semibold flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 transition-all ${mode === "visitor" ? "bg-teal-600 text-white shadow-lg" : "bg-teal-50 text-slate-600 border border-teal-200"}`}>
                  <QrCode className="w-4 h-4 shrink-0" /> {t.portalPage.visitor}
                </button>
                <Link to="/account" data-testid="portal-tab-member"
                  className="px-2 sm:px-4 py-3 sm:py-3.5 rounded-xl text-[11px] sm:text-sm font-semibold flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 transition-all bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100">
                  <UserPlus className="w-4 h-4 shrink-0" /> {t.portalPage.member}
                </Link>
              </div>
              {mode === "employee" ? <EmployeeLogin login={login} navigate={navigate} t={t} /> : <VisitorPass t={t} />}
            </div>
          </Reveal>
        </div>

        <div className="mt-8 oj-card p-5 flex gap-3 items-start border-amber-400/20">
          <ShieldCheck className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600 leading-relaxed"><span className="font-semibold text-slate-600">{p.advisoryTitle}</span> {p.advisory}</p>
        </div>
      </section>
    </div>
  );
}

function EmployeeLogin({ login, navigate, t }) {
  const p = t.portalX;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dept, setDept] = useState(p.depts[0]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(`${p.welcome}, ${data.name}`);
      navigate("/admin");
    } catch (err) { toast.error(apiError(err, "Invalid credentials")); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} data-testid="employee-login-form">
      <h3 className="font-bold text-white text-lg mb-1">{p.empTitle}</h3>
      <p className="text-sm text-slate-600 mb-6">{p.empSub}</p>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 block">{p.emailLabel}</label>
          <div className="relative"><User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500" /><input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@oojjascorporate.com" className="oj-input pl-11" data-testid="portal-email" /></div>
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 block">{p.passLabel}</label>
          <div className="relative">
            <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500" />
            <input required type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••" className="oj-input pl-11 pr-11" data-testid="portal-password" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-teal-600">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          </div>
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 block">{p.deptLabel}</label>
          <select value={dept} onChange={(e) => setDept(e.target.value)} className="oj-input" data-testid="portal-dept">
            {p.depts.map((d) => <option key={d} className="bg-teal-950">{d}</option>)}
          </select>
        </div>
        <button disabled={loading} className="oj-btn-gold w-full" data-testid="portal-login-submit-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t.portalPage.signIn}
        </button>
        <p className="text-xs text-slate-500 text-center">{p.demo}</p>
      </div>
    </form>
  );
}

function VisitorPass({ t }) {
  const p = t.portalX;
  const [form, setForm] = useState({ full_name: "", company: "", host_email: "", purpose: "", id_type: ID_TYPES[0], phone: "" });
  const [pass, setPass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifyMode, setVerifyMode] = useState(false);
  const [vcode, setVcode] = useState("");
  const [vresult, setVresult] = useState(null);
  const [vbusy, setVbusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/visitor-pass", form);
      setPass(data);
      toast.success(p.passActive);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setVbusy(true);
    setVresult(null);
    try {
      const { data } = await api.get(`/visitor-pass/lookup/${encodeURIComponent(vcode.trim())}`);
      setVresult(data);
    } catch (err) { toast.error(apiError(err, "Pass not found")); }
    finally { setVbusy(false); }
  };

  const printPass = () => {
    const svg = document.getElementById("visitor-qr-svg");
    if (!svg || !pass) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const qrSrc = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(xml)))}`;
    const w = window.open("", "_blank", "width=420,height=680");
    if (!w) { toast.error("Popup blocked — allow popups to print"); return; }

    // Build the print document with DOM APIs only (no document.write / HTML injection).
    // User-supplied fields are set via textContent, so they can never execute as markup.
    const doc = w.document;
    doc.title = `OOJJAS Visitor Pass ${pass.pass_code}`;
    const style = doc.createElement("style");
    style.textContent = "body{font-family:'Nunito',Arial,sans-serif;text-align:center;padding:32px;color:#0B3B39}.code{font-family:monospace;font-size:26px;font-weight:800;color:#b45309;letter-spacing:2px}.muted{color:#64748b;font-size:12px;margin-top:10px}.box{border:2px solid #017471;border-radius:16px;padding:24px;max-width:320px;margin:0 auto}";
    doc.head.appendChild(style);

    const box = doc.createElement("div");
    box.className = "box";

    const logo = doc.createElement("img");
    logo.src = `${window.location.origin}/assets/oojjas-logo-tagline.jpg`;
    logo.style.maxWidth = "200px";
    logo.style.marginBottom = "14px";
    logo.alt = "OOJJAS";
    box.appendChild(logo);

    const qrImg = doc.createElement("img");
    qrImg.src = qrSrc; qrImg.width = 180; qrImg.height = 180; qrImg.alt = "QR";
    box.appendChild(qrImg);

    const code = doc.createElement("div");
    code.className = "code"; code.textContent = pass.pass_code;
    box.appendChild(code);

    const name = doc.createElement("div");
    name.style.fontWeight = "800"; name.style.marginTop = "4px"; name.textContent = pass.full_name;
    box.appendChild(name);

    const company = doc.createElement("div");
    company.className = "muted"; company.style.marginTop = "2px"; company.textContent = pass.company;
    box.appendChild(company);

    const valid = doc.createElement("div");
    valid.className = "muted"; valid.textContent = `${p.validUntil} ${new Date(pass.expires_at).toLocaleString()}`;
    box.appendChild(valid);

    const note = doc.createElement("div");
    note.className = "muted"; note.textContent = p.presentQr;
    box.appendChild(note);

    doc.body.appendChild(box);
    qrImg.onload = () => w.print();
    setTimeout(() => { try { w.print(); } catch (err) { console.debug("Print fallback skipped:", err); } }, 500);
  };

  if (pass) {
    const qrData = JSON.stringify({ code: pass.pass_code, name: pass.full_name, expires: pass.expires_at, id: pass.id });
    return (
      <div className="text-center" data-testid="visitor-pass-result">
        <div className="inline-flex items-center gap-2 text-teal-400 text-sm font-semibold mb-4"><CheckCircle2 className="w-5 h-5" /> {p.passActive}</div>
        <div className="mx-auto w-fit bg-white p-5 rounded-2xl mb-5">
          <QRCodeSVG id="visitor-qr-svg" value={qrData} size={180} level="H" fgColor="#014845" />
        </div>
        <div className="font-mono text-2xl font-bold gold-text mb-1">{pass.pass_code}</div>
        <div className="text-white font-semibold">{pass.full_name}</div>
        <div className="text-sm text-slate-600">{pass.company}</div>
        <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-amber-300 font-mono"><Clock className="w-3.5 h-3.5" /> {p.validUntil} {new Date(pass.expires_at).toLocaleString()}</div>
        <p className="mt-4 text-xs text-slate-500">{p.presentQr}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <button onClick={printPass} className="oj-btn-gold !py-2.5 !text-sm" data-testid="pass-print-button"><Printer className="w-4 h-4" /> {p.print}</button>
          <button onClick={() => setPass(null)} className="oj-btn-ghost !py-2.5 !text-sm" data-testid="visitor-new-pass">{p.another}</button>
        </div>
      </div>
    );
  }

  if (verifyMode) {
    const expired = vresult && new Date(vresult.expires_at) < new Date();
    return (
      <div data-testid="gate-verify">
        <h3 className="font-bold text-white text-lg mb-1">{p.verifyTitle}</h3>
        <p className="text-sm text-slate-600 mb-6">{p.verifySub}</p>
        <form onSubmit={verify} className="flex gap-3 mb-5">
          <input required placeholder={p.verifyPh} value={vcode} onChange={(e) => setVcode(e.target.value)} className="oj-input font-mono uppercase" data-testid="verify-code-input" />
          <button disabled={vbusy} className="oj-btn-gold !py-3 !px-5 shrink-0" data-testid="verify-submit">
            {vbusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />} {p.verifyBtn}
          </button>
        </form>
        {vresult && (
          <div className={`rounded-2xl border p-5 ${expired ? "bg-red-500/10 border-red-500/40" : "bg-teal-500/10 border-teal-400/40"}`} data-testid="verify-result">
            <div className={`inline-flex items-center gap-2 font-extrabold uppercase tracking-wide text-sm ${expired ? "text-red-400" : "text-teal-400"}`} data-testid="verify-status">
              {expired ? <><Lock className="w-4 h-4" /> {p.expired}</> : <><CheckCircle2 className="w-4 h-4" /> {p.activeOk}</>}
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="text-white font-bold font-mono">{vresult.pass_code}</div>
              <div className="text-white font-semibold">{vresult.full_name}</div>
              <div className="text-slate-600 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {vresult.company} • {vresult.purpose}</div>
              <div className="text-amber-300 text-xs font-mono flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {p.validUntil} {new Date(vresult.expires_at).toLocaleString()}</div>
            </div>
          </div>
        )}
        <button onClick={() => { setVerifyMode(false); setVresult(null); setVcode(""); }} className="oj-btn-ghost mt-5 !py-2.5 !text-sm" data-testid="verify-back">{p.backToPass}</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="visitor-pass-form">
      <h3 className="font-bold text-white text-lg mb-1">{p.visitorTitle}</h3>
      <p className="text-sm text-slate-600 mb-6">{p.visitorSub}</p>
      <div className="space-y-4">
        <input required placeholder={p.ph.name} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="oj-input" data-testid="visitor-name" />
        <input required placeholder={p.ph.company} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="oj-input" data-testid="visitor-company" />
        <input required type="email" placeholder={p.ph.host} value={form.host_email} onChange={(e) => setForm({ ...form, host_email: e.target.value })} className="oj-input" data-testid="visitor-host" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input placeholder={p.ph.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="oj-input" data-testid="visitor-phone" />
          <select value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })} className="oj-input" data-testid="visitor-idtype">
            {ID_TYPES.map((id) => <option key={id} className="bg-teal-950">{id}</option>)}
          </select>
        </div>
        <input required placeholder={p.ph.purpose} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="oj-input" data-testid="visitor-purpose" />
        <button disabled={loading} className="oj-btn-gold w-full" data-testid="visitor-generate-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />} {p.generate}
        </button>
        <button type="button" onClick={() => setVerifyMode(true)} className="w-full text-center text-xs font-semibold text-teal-400 hover:text-amber-300 transition-colors" data-testid="verify-toggle">
          {p.gateStaff}
        </button>
      </div>
    </form>
  );
}
